import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import DjehootyFile from './file';
import FileInstance from './file_instance';
import Individual from './individual';

import Thumbnail, { resolveThumbnailPath, resolveThumbnailPathFromCategory, ThumbnailCategory, ThumbnailStatus } from './thumbnail';

/**
 * Interface representing the updatable fields of a Document
 */
export interface DocumentUpdateData {
    title?: string;
    description?: string;
    source?: string;
}

// This class represents a Djehooty document, which can contain multiple file instances.
export default class DjehootyDocument {
    id: number;
    documentType: number;
    title: string;
    thumbnail: Thumbnail;
    source: string;
    description: string;
    file_count: number;
    createdAt: string;
    agentID: number;
    file_instances: FileInstance[];
    individuals: Individual[];

    constructor(
        id: number = 0,
        documentType: number = 0,
        title: string = "",
        thumbnail: Thumbnail = new Thumbnail(),
        source: string = "",
        description: string = "",
        file_count: number = 0,
        createdAt: string = "",
        agentID: number = 0,
        file_instances: FileInstance[] = [],
        individuals: Individual[] = [],
    ) {
        this.id = id;
        this.documentType = documentType;
        this.title = title;
        this.thumbnail = thumbnail;
        this.source = source;
        this.description = description;
        this.file_count = file_count;
        this.createdAt = createdAt;
        this.agentID = agentID;
        this.file_instances = file_instances;
        this.individuals = individuals;
    }

    // TODO : it seems a bit dirty to me, to be checked
    loadFromJson(jsonPayload: { id: number; documentType: number; title: string; thumbnail: { file_thumbnail_id: number; url: string; status: ThumbnailStatus, category: ThumbnailCategory }; source: string; description: string; file_count: number; created_at: string; agentID: number; instances: any[]; individuals: any[]; }) {
        this.id = jsonPayload.id;
        this.documentType = jsonPayload.documentType;
        this.title = jsonPayload.title;
        this.thumbnail = new Thumbnail(jsonPayload.thumbnail.file_thumbnail_id, jsonPayload.thumbnail.url, jsonPayload.thumbnail.status, jsonPayload.thumbnail.category);
        this.source = jsonPayload.source;
        this.description = jsonPayload.description;
        this.file_count = jsonPayload.file_count;
        this.createdAt = jsonPayload.created_at;
        this.agentID = jsonPayload.agentID;

        this.file_instances = jsonPayload.instances.map((instance: { id: number; position: number; filename: string; filename_orig: string; imported_at: string; modified_at: string; agent_id: number; file: DjehootyFile; }) => {
            const fileInstance = new FileInstance();
            fileInstance.loadFromJson(instance);
            return fileInstance;
        });

        this.individuals = Array.isArray(jsonPayload.individuals)
            ? jsonPayload.individuals.map((indiv: { id: number; firstname: string; lastname: string; occupation: string; wikipedia_url: string; wikidata_id: string; thumbnail: { file_thumbnail_id: number; url: string; status: ThumbnailStatus }; }) => {
                const individual = new Individual();
                individual.loadFromJson(indiv);
                return individual;
            })
            : [];
    }

    /**
     * Gets the thumbnail status of the document
     * @returns ThumbnailStatus
     */
    getThumbnailStatus = (): ThumbnailStatus => {
        if (!this.thumbnail) {
            return ThumbnailStatus.None;
        } else {
            return this.thumbnail.status;
        }
    }

    /**
     * Gets the thumbnail path of the document
     * @returns string - The thumbnail path
     */
    getThumbnailPath = (): string => {
        // We don't have media type at document level, so we use "unknown" as media type for thumbnail resolution
        // in case of missing thumbnail. It should not happen though, since tumbnail is selected by the user.

        // Missing thumbnail structure. It should not happen, but in this case we return a default thumbnail for unknown media type
        if (!this.thumbnail) {
            return resolveThumbnailPath(ThumbnailStatus.None, "", ThumbnailCategory.Other);
        }

        return this.thumbnail.getPath();
    }

    getAltText = (): string => {
        if (this.title === undefined) {
            return "Document"
        } else {
            return this.title
        }
    }

    isMultipleFiles = (): boolean => {
        if (this.file_instances.length > 1) {
            return true
        }
        return false
    }

    totalSizeHuman = (): string => {
        var size: number = 0
        this.file_instances.forEach((inst: FileInstance) => {
            size += inst.file.size
        });

        if (size < 1024) {
            return size + "B"
        }
        var kBytes: number = size / 1024;
        if (kBytes < 1024) {
            return kBytes.toFixed(2) + "kB"
        }
        var MBytes: number = kBytes / 1024;
        if (MBytes < 1024) {
            return MBytes.toFixed(2) + "MB"
        }
        var GBytes: number = MBytes / 1024;
        return GBytes.toFixed(2) + "GB"
    }

    /**
     * Fetches a document from the API.
     * Returns null if the document is not found or the format is invalid.
     * @param id - Document ID (must be > 0)
     * @returns Promise<DjehootyDocument | null>
     */
    static async fetchDocument(id: number): Promise<DjehootyDocument | null> {
        try {
            if (id <= 0 || isNaN(id)) {
                throw new Error("Invalid document ID");
            }

            const url = API_BASE_URL + '/documents/' + id
            const response = await axios.get(url);

            if (!response.data || typeof response.data !== 'object' || !response.data.id) {
                console.warn("API returned no document or invalid format");
                return null;
            }

            const document = new DjehootyDocument();
            document.loadFromJson(response.data);
            return document;
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            const timestamp = new Date().toISOString();
            console.error(`[${timestamp}] Error fetching document ${id} - (Code ${apiCode}) : ${apiMessage} - Path:`, apiPath);

            // If document not found (404), return null instead of throwing
            if (error.response?.status === 404) {
                return null;
            }

            // Simplified message for user. We don't expose all technical api errors
            throw new Error("Error fetching document");
        }
    }

    /**
     * Fetches a list of documents from the API.
     * @returns Promise<DjehootyDocument[]>
     */
    static async fetchAll(search: string): Promise<DjehootyDocument[]> {
        try {
            var url: string
            if (search.length === 0) {
                url = `${API_BASE_URL}/documents`
            } else {
                url = `${API_BASE_URL}/documents?q=${search}`
            }
            const response = await axios.get(url);
            if (!Array.isArray(response.data)) {
                console.warn("API returned no documents or invalid format");
                return [];
            }

            return response.data.map((item: any) => {
                // To be able to call methods from DjehootyFile, we need to instanciate DjehootyFile objects
                const document = new DjehootyDocument();
                document.loadFromJson(item);
                return document;
            });
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error fetching documents - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Simplified message for user. We don't expose all technical api errors
            throw new Error("Error fetching documents");
        }
    }

    /**
     * Update a document
     * @param documentId - Document id
     * @param updateData - Data to update (at least one field must be provided)
     * @returns Promise<DjehootyDocument> : the updated document
     * @throws Error if creation fails or response is invalid.
     */
    static async update(documentId: number, updateData: DocumentUpdateData): Promise<DjehootyDocument> {
        if (documentId <= 0) {
            throw new Error("Invalid document ID");
        }

        // Validate that at least one field is provided
        if (Object.keys(updateData).length === 0) {
            throw new Error("No updateData provided for update");
        }

        // Transform camelCase to snake_case for API
        const apiData: Record<string, any> = {};
        if (updateData.title !== undefined) apiData.title = updateData.title;
        if (updateData.description !== undefined) apiData.description = updateData.description;
        if (updateData.source !== undefined) apiData.source = updateData.source;

        try {
            console.log("Updating document:", documentId, "with data:", apiData);

            const response = await axios.patch(`${API_BASE_URL}/documents/${documentId}`, apiData);

            if (response.data && typeof response.data === 'object' && response.data.id) {
                const document = new DjehootyDocument();
                document.loadFromJson(response.data);
                return document;
            } else {
                throw new Error("Unexpected response from server during update operation");
            }
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;
            console.error('Error updating document - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            throw new Error(apiMessage || "Error updating document");
        }
    }

    /**
     * Identifies an individual in a document.
     * @param documentId - The ID of the document.
     * @param individualId - The ID of the individual to identify.
     * @returns void
     */
    static async identifyIndividualAdd(documentId: number, individualId: number): Promise<void> {
        if (isNaN(documentId) || documentId <= 0 ||
            isNaN(individualId) || individualId <= 0) {
            throw new Error(`identifyIndividual: invalid parameters`);
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/documents/${documentId}/identifications`, {
                type: "individual",
                id: individualId,
            });
            return
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;

            console.error('Error identifying individual - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error identifying individual.");
        }
    }

    /**
     * Removes an individual identification from a document.
     * @param documentId - The ID of the document.
     * @param individualId - The ID of the individual to remove.
     * @returns void
     */
    static async identifyIndividualRemove(documentId: number, individualId: number): Promise<void> {
        if (isNaN(documentId) || documentId <= 0 ||
            isNaN(individualId) || individualId <= 0) {
            throw new Error(`identifyIndividual: invalid parameters`);
        }

        try {
            // Success is based solely on status code
            // We expect a 204 status code (No content)
            await axios.delete(`${API_BASE_URL}/documents/${documentId}/identifications`, {
                data: {
                    type: "individual",
                    id: individualId,
                },
            });
        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;

            console.error('Error removing individual identification - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error removing individual identification.");
        }
    }

    /**
     * Merges multiple DjehootyDocument instances into a single document.
     * @param documents - Array of DjehootyDocument to merge.
     * @returns Promise resolving to the merged DjehootyDocument.
     * @throws Error if merge fails or response is invalid.
     */
    static async merge(documents: DjehootyDocument[]): Promise<DjehootyDocument> {
        if (documents.length < 2) {
            throw new Error(`Document list is too small`);
        }

        try {
            const selectedIds = documents.map((doc) => doc.id);
            const response = await axios.post(`${API_BASE_URL}/documents/merge`, {
                documents: selectedIds
            });

            // If the response is the merged document, instantiate it
            // and return merged document
            if (response.data && typeof response.data === 'object' && response.data.id) {
                const mergedDoc = new DjehootyDocument();
                mergedDoc.loadFromJson(response.data);
                return mergedDoc;
            }

            // Unexpected response format
            throw new Error("Unexpected response from server during merge operation");

        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;

            console.error('Error merging document - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error merging document.");
        }
    }

    /**
     * Splits a single DjehootyDocument into several documents
     * @param documentID - ID ot the DjehootyDocument to split
     * @returns Promise resolving to the splitted DjehootyDocument.
     * @throws Error if split fails or response is invalid.
     */
    static async split(documentID: number): Promise<void> {
        if (documentID <= 0) {
            throw new Error(`documentID must be > 0`);
        }

        try {
            // Success is based solely on status code
            // We expect a 200 status code (OK)
            await axios.post(`${API_BASE_URL}/documents/${documentID}/split`);

            // TODO : Improve return value when backend will support document list as return value

        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;

            console.error('Error splitting document - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error splitting document.");
        }
    }

    /**
     * Delete one or multiple DjehootyDocuments
     * @param documents - Array of DjehootyDocument to delete.
     * @returns Promise<void> : 2xx code is ok, other will throw errors
     * @throws Error if deletion fails or response is invalid.
     */
    static async delete(documents: DjehootyDocument[]): Promise<void> {
        if (documents.length < 1) {
            throw new Error(`Document list can't be void`);
        }

        try {
            // Success is based solely on status code
            // We expect a 204 status code (No content)
            if (documents.length === 1) {
                await axios.delete(`${API_BASE_URL}/documents/${documents[0].id}`);
                return;
            } else if (documents.length > 1) {
                const selectedIds = documents.map((doc) => doc.id);
                const response = await axios.delete(`${API_BASE_URL}/documents`, {
                    data: {
                        documents: selectedIds
                    }
                });
                return;
            }

            // Unexpected response format
            throw new Error("Unexpected response from server during deletion operation");

        } catch (error: any) {
            const apiMessage = error.response?.data?.message;
            const apiCode = error.response?.data?.code;
            const apiPath = error.response?.data?.path;

            console.error('Error deleting document - (Code ' + apiCode + ') : ' + apiMessage + ' - Path:', apiPath);

            // Propagate error
            throw new Error(apiMessage || "Error deleting document.");
        }
    }
}